import ConfirmDialog from '@/components/ui/ConfirmDialog';
import ChoiceField from '@/components/ui/ChoiceField';
import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Plus, Edit2, Trash2, Check, Clock, Circle, ChevronDown, ChevronUp, User, Calendar as CalendarIcon, FileText } from 'lucide-react';
import { useOrganisation } from '@/lib/OrganisationContext';
import AssessmentSourceDialog from '@/components/roadmap/AssessmentSourceDialog';
import CalendarView from '@/components/roadmap/CalendarView';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const LEVEL_CONFIG = {
  1: { label: 'Committed', badge: 'bg-slate-600 text-white', ring: 'bg-slate-500' },
  2: { label: 'Employer', badge: 'bg-teal-600 text-white', ring: 'bg-teal-500' },
  3: { label: 'Leader', badge: 'bg-amber-500 text-white', ring: 'bg-amber-500' },
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', icon: Circle, color: 'text-slate-400', bg: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In Progress', icon: Clock, color: 'text-amber-500', bg: 'bg-amber-100 text-amber-700' },
  complete: { label: 'Complete', icon: Check, color: 'text-teal-500', bg: 'bg-teal-100 text-teal-700' },
};

export default function Roadmap() {
  const { selectedOrg } = useOrganisation();
  const [tasks, setTasks] = useState([]);
  const [deletePrompt, setDeletePrompt] = useState(null);
  const deleteReturnRef = useRef(null);
  const addTaskRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const loadController = useRef(null);
  const mounted = useRef(false);
  const [expandedLevels, setExpandedLevels] = useState({ 1: true, 2: true, 3: true });
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [sourceTask, setSourceTask] = useState(null);
  const sourceReturnRef = useRef(null);
  const [track, setTrack] = useState('dc');
  const [generatingCI, setGeneratingCI] = useState(false);
  const [liveMsg, setLiveMsg] = useState('');

  useEffect(() => {
    mounted.current = true;
    setDeletePrompt(null); setSourceTask(null); loadAll();
    return () => { mounted.current = false; loadController.current?.abort(); };
  }, [selectedOrg?.id]);

  async function loadAll() {
    if (!mounted.current) return;
    loadController.current?.abort();
    const controller = new AbortController(); loadController.current = controller;
    setLoading(true); setLoadError(false); setTasks([]);
    try {
      const allTasks = await base44.roadmapTasks.listAll(selectedOrg?.id, { signal: controller.signal });
      if (!controller.signal.aborted) setTasks(allTasks);
    } catch {
      if (!controller.signal.aborted) setLoadError(true);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  async function deleteTask(taskId) {
    await base44.entities.RoadmapTask.delete(taskId);
    setTasks(prev => prev.filter(t => t.id !== taskId));
    setLiveMsg('Task deleted');
    deleteReturnRef.current = addTaskRef.current;
  }

  function showSource(task, event) {
    sourceReturnRef.current = event.currentTarget;
    setSourceTask({ task, organisationId: selectedOrg?.id });
  }

  function toggleLevel(lvl) {
    setExpandedLevels(prev => ({ ...prev, [lvl]: !prev[lvl] }));
  }

  function getProgress(levelTasks) {
    if (!levelTasks.length) return 0;
    const done = levelTasks.filter(t => t.status === 'complete').length;
    return Math.round((done / levelTasks.length) * 100);
  }

  const trackTasks = tasks.filter(t => (t.track || 'dc') === track);

  async function generateCIActions() {
    setGeneratingCI(true);
    try {
      const assessments = await base44.entities.Assessment.filter(
        selectedOrg?.id ? { organisation_id: selectedOrg.id, status: 'completed' } : { status: 'completed' },
        '-created_date', 1
      );
      const achievedLevel = assessments[0]?.current_level || 0;
      await base44.functions.invoke('generateContinuousImprovementTasks', {
        achieved_level: achievedLevel || 3,
        sector: selectedOrg?.sector || 'general',
        organisation_id: selectedOrg?.id,
      });
      await loadAll();
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingCI(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" aria-label="Loading roadmap" />
      </div>
    );
  }

  if (loadError) return <div className="rounded-lg border border-border bg-card p-6 text-foreground">
    <p role="alert">The roadmap tasks could not be loaded. Please try again.</p>
    <button type="button" onClick={loadAll} className="mt-4 min-h-11 rounded-lg border border-border px-4 font-semibold">Retry loading</button>
  </div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {deletePrompt && <ConfirmDialog title={`Delete ${deletePrompt.title}?`} description="This removes the task from the roadmap. Its linked evidence documents remain in the vault." action="Delete task" returnFocusRef={deleteReturnRef} onClose={() => setDeletePrompt(null)} onConfirm={() => deleteTask(deletePrompt.id)} />}
      <div className="sr-only" aria-live="polite" aria-atomic="true">{liveMsg}</div>

      {sourceTask && sourceTask.organisationId === selectedOrg?.id && <AssessmentSourceDialog key={`${sourceTask.organisationId}:${sourceTask.task.id}`} task={sourceTask.task} organisationId={sourceTask.organisationId} onClose={() => setSourceTask(null)} returnFocusRef={sourceReturnRef} />}

      {/* Track toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setTrack('dc')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ${track === 'dc' ? 'bg-teal-600 text-white' : 'bg-card border border-border text-muted-foreground hover:bg-muted'}`}
        >
          Disability Confident Roadmap
        </button>
        <button
          onClick={() => setTrack('ci')}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ${track === 'ci' ? 'bg-teal-600 text-white' : 'bg-card border border-border text-muted-foreground hover:bg-muted'}`}
        >
          Continuous Improvement
          {tasks.filter(t => t.track === 'ci').length > 0 && (
            <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{tasks.filter(t => t.track === 'ci').length}</span>
          )}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{trackTasks.length} {track === 'dc' ? 'tasks across 3 levels' : 'continuous improvement actions'}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCalendar(true)}
            aria-haspopup="dialog"
            aria-label="View task calendar"
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card text-sm font-medium text-foreground hover:bg-muted transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <CalendarIcon className="w-4 h-4" aria-hidden="true" />
            Task Calendar
          </button>
          <button
            ref={addTaskRef}
            onClick={() => { setEditingTask(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
            aria-haspopup="dialog"
          >
            <Plus className="w-4 h-4" aria-hidden="true" />
            Add Task
          </button>
        </div>
      </div>

      {track === 'dc' && trackTasks.length === 0 && (
        <div className="text-center py-16 bg-card border border-dashed border-border rounded-2xl">
          <p className="text-muted-foreground text-sm">No tasks yet. Complete an assessment to auto-generate your roadmap, or add tasks manually.</p>
        </div>
      )}

      {track === 'dc' && [1, 2, 3].map(level => {
        const cfg = LEVEL_CONFIG[level];
        const levelTasks = trackTasks.filter(t => t.level === level);
        const pct = getProgress(levelTasks);
        const expanded = expandedLevels[level];

        const byTheme = levelTasks.reduce((acc, t) => {
          if (!acc[t.theme]) acc[t.theme] = [];
          acc[t.theme].push(t);
          return acc;
        }, {});

        return (
          <section key={level} aria-labelledby={`level-${level}-heading`} className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={() => toggleLevel(level)}
              aria-expanded={expanded}
              aria-controls={`level-${level}-content`}
              className="w-full flex items-center justify-between p-5 hover:bg-muted/50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              <div className="flex items-center gap-4">
                <span id={`level-${level}-heading`} className={`text-xs font-bold px-2.5 py-1 rounded-full ${cfg.badge}`}>Level {level} — {cfg.label}</span>
                <span className="text-sm text-muted-foreground">{levelTasks.length} tasks · {pct}% complete</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-2 w-40">
                  <div className="flex-1 bg-muted rounded-full h-1.5" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Level ${level} progress`}>
                    <div className={`h-1.5 rounded-full ${level === 1 ? 'bg-slate-500' : level === 2 ? 'bg-teal-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-foreground w-8 text-right">{pct}%</span>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />}
              </div>
            </button>

            {expanded && (
              <div id={`level-${level}-content`} className="border-t border-border">
                {Object.keys(byTheme).length === 0 ? (
                  <p className="text-sm text-muted-foreground p-5">No tasks at this level yet.</p>
                ) : (
                  Object.entries(byTheme).map(([theme, themeTasks]) => (
                    <div key={theme} className="border-b border-border last:border-0">
                      <div className="px-5 py-3 bg-muted/30">
                        <h3 className="text-xs font-semibold text-teal-700 uppercase tracking-wide">{theme}</h3>
                      </div>
                      <ul>
                        {themeTasks.map(task => (
                          <TaskRow
                            key={task.id}
                            task={task} onSource={event => showSource(task, event)}
                            onEdit={() => { setEditingTask(task); setShowForm(true); }}
                            onDelete={event => { deleteReturnRef.current = event.currentTarget; setDeletePrompt(task); }}
                          />
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        );
      })}

      {track === 'ci' && (
        <div className="space-y-4">
          {trackTasks.length === 0 ? (
            <div className="text-center py-12 bg-card border border-dashed border-border rounded-2xl">
              <p className="text-sm text-muted-foreground mb-4">No continuous improvement actions yet.</p>
              <button
                onClick={generateCIActions}
                disabled={generatingCI}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {generatingCI ? 'Generating…' : 'Generate CI Actions'}
              </button>
              <p className="text-xs text-muted-foreground mt-3">AI-suggested actions to maintain and build on your Disability Confident achievements.</p>
            </div>
          ) : (
            <>
              <div className="flex justify-end">
                <button
                  onClick={generateCIActions}
                  disabled={generatingCI}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors"
                >
                  {generatingCI ? 'Generating…' : 'Regenerate CI Actions'}
                </button>
              </div>
              {Object.entries(
                trackTasks.reduce((acc, t) => {
                  const theme = t.theme || 'Continuous Improvement';
                  if (!acc[theme]) acc[theme] = [];
                  acc[theme].push(t);
                  return acc;
                }, {})
              ).map(([theme, themeTasks]) => (
                <div key={theme} className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-5 py-3 bg-muted/30">
                    <h3 className="text-xs font-semibold text-teal-700 uppercase tracking-wide">{theme}</h3>
                  </div>
                  <ul>
                    {themeTasks.map(task => (
                      <TaskRow
                        key={task.id}
                        task={task} onSource={event => showSource(task, event)}
                        onEdit={() => { setEditingTask(task); setShowForm(true); }}
                        onDelete={event => { deleteReturnRef.current = event.currentTarget; setDeletePrompt(task); }}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {showForm && (
        <TaskForm
          task={editingTask}
          defaultTrack={track}
          onClose={() => { setShowForm(false); setEditingTask(null); }}
          onSave={async (data) => {
            if (editingTask) {
              await base44.entities.RoadmapTask.update(editingTask.id, data);
              setTasks(prev => prev.map(t => t.id === editingTask.id ? { ...t, ...data } : t));
            } else {
              const created = await base44.entities.RoadmapTask.create({ ...data, organisation_id: selectedOrg?.id, track: data.track || track });
              setTasks(prev => [...prev, created]);
            }
            setShowForm(false);
            setEditingTask(null);
            setLiveMsg(editingTask ? 'Task updated' : 'Task added');
          }}
        />
      )}

      {showCalendar && (
        <CalendarView tasks={tasks.filter(t => t.due_date)} onClose={() => setShowCalendar(false)} />
      )}
    </div>
  );
}

function TaskRow({ task, onEdit, onDelete, onSource }) {
  const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG.not_started;
  const StatusIcon = statusCfg.icon;
  const isOverdue = task.due_date && task.status !== 'complete' && new Date(task.due_date) < new Date();

  return (
    <li className="flex items-start gap-4 px-5 py-3.5 hover:bg-muted/20 border-b border-border/50 last:border-0 group">
      <span
        className={`mt-0.5 shrink-0 ${statusCfg.color}`}
        aria-label={`Status: ${statusCfg.label}`}
      >
        <StatusIcon className="w-5 h-5" aria-hidden="true" />
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{task.title}</p>
        <button type="button" onClick={onSource} aria-haspopup="dialog" aria-label={`Assessment source: ${task.title}`} className="min-h-11 py-2 text-sm underline text-foreground">Assessment source</button>
        {task.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{task.description}</p>}
        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${statusCfg.bg}`}>{statusCfg.label}</span>
          {task.owner_name && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <User className="w-3 h-3" aria-hidden="true" />
              {task.owner_name}
            </span>
          )}
          {task.due_date && (
            <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
              <CalendarIcon className="w-3 h-3" aria-hidden="true" />
              {isOverdue ? 'Overdue · ' : ''}{task.due_date}
            </span>
          )}
          {task.evidence_required && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <FileText className="w-3 h-3" aria-hidden="true" />
              Evidence needed
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button onClick={onEdit} aria-label={`Edit task: ${task.title}`} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          <Edit2 className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
        <button onClick={onDelete} aria-label={`Delete task: ${task.title}`} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

function TaskForm({ task, defaultTrack, onClose, onSave }) {
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    level: task?.level || 1,
    theme: task?.theme || '',
    owner_name: task?.owner_name || '',
    due_date: task?.due_date || '',
    status: task?.status || 'not_started',
    evidence_required: task?.evidence_required || '',
    track: task?.track || defaultTrack || 'dc',
  });
  const CI_THEMES = ['Inclusive Recruitment', 'Guaranteed Interview', 'Reasonable Adjustments', 'Supporting Employees', 'Practical Activity', 'Getting the Right People', 'Keeping and Developing People', 'Self-Assessment', 'Leadership & Strategy', 'External Validation', 'Reporting & Data', 'Continuous Improvement'];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const firstRef = useRef(null);
  const openerRef = useRef(typeof document === 'undefined' ? null : document.activeElement);

  const THEMES = {
    1: ['Inclusive Recruitment', 'Guaranteed Interview', 'Reasonable Adjustments', 'Supporting Employees', 'Practical Activity'],
    2: ['Getting the Right People', 'Keeping and Developing People', 'Self-Assessment'],
    3: ['Leadership & Strategy', 'External Validation', 'Reporting & Data'],
  };

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try { await onSave({ ...form, due_date: form.due_date || null }); }
    catch (err) { setError(err.message || 'The task was not saved. Check your connection and try again.'); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent returnFocusRef={openerRef} className="max-h-[90vh] overflow-y-auto rounded-2xl" onOpenAutoFocus={(event) => { event.preventDefault(); firstRef.current?.focus(); }}>
        <DialogHeader><DialogTitle>{task ? 'Edit task' : 'Add task'}</DialogTitle><DialogDescription>Create a roadmap action. Task title, level and theme are required.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="task-title" className="block text-sm font-medium text-foreground mb-1">Task Title <span aria-hidden="true">*</span></label>
            <input ref={firstRef} id="task-title" required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" />
          </div>
          <div>
            <label htmlFor="task-desc" className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea id="task-desc" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring resize-none" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceField label="Level" required value={form.level} options={[[1, 'Level 1 — Committed'], [2, 'Level 2 — Employer'], [3, 'Level 3 — Leader']]} onChange={level => setForm(p => ({ ...p, level, theme: (p.track === 'ci' ? CI_THEMES : (THEMES[level] || [])).includes(p.theme) ? p.theme : '' }))} />
            <ChoiceField label="Theme" required value={form.theme} options={(form.track === 'ci' ? CI_THEMES : (THEMES[form.level] || [])).map(t => [t, t])} onChange={theme => setForm(p => ({ ...p, theme }))} />
            <div>
              <label htmlFor="task-owner" className="block text-sm font-medium text-foreground mb-1">Owner</label>
              <input id="task-owner" value={form.owner_name} onChange={e => setForm(p => ({ ...p, owner_name: e.target.value }))} placeholder="Name or team" className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" />
            </div>
            <div>
              <label htmlFor="task-due" className="block text-sm font-medium text-foreground mb-1">Due Date</label>
              <input id="task-due" type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" />
            </div>
          </div>
          <div>
            <label htmlFor="task-status" className="block text-sm font-medium text-foreground mb-1">Status</label>
            <select id="task-status" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
            </select>
          </div>
          <div>
            <label htmlFor="task-evidence" className="block text-sm font-medium text-foreground mb-1">Evidence Required</label>
            <input id="task-evidence" value={form.evidence_required} onChange={e => setForm(p => ({ ...p, evidence_required: e.target.value }))} placeholder="e.g. Recruitment Policy document" className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" />
          </div>
          {error && <p role="alert" className="accessible-error rounded-lg border border-red-500 bg-background p-3 text-sm text-foreground">{error}</p>}
          {saving && <p role="status" className="text-sm text-muted-foreground">Saving task…</p>}
          <DialogFooter className="gap-3 pt-2 sm:space-x-0">
            <DialogClose asChild><button type="button" disabled={saving} className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted">Cancel</button></DialogClose>
            <button type="submit" disabled={saving} className="flex-1 px-5 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400">
              {saving ? 'Saving…' : task ? 'Save changes' : 'Add task'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
